// Inspired by react-hot-toast library
import * as React from 'react';

import type { ToastProps } from '@/components/ui/toast';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;
// How long a toast stays visible before it auto-dismisses. Kept in sync with the 5s
// `reduce-width` progress-bar animation in toast.tsx. We drive the dismissal ourselves
// (below) rather than relying on Radix's built-in `duration` timer, which resets on every
// re-render and pauses on hover/window-blur - in a busy app that timer often never fires,
// leaving the toast stuck on screen until it's closed manually.
const TOAST_AUTO_DISMISS_DELAY = 5000;

type ToasterToast = ToastProps & {
	id: string;
	title?: React.ReactNode;
	description?: React.ReactNode;
	// No `action`: a toast never carries a button. It vanishes in 5s, so anything the user
	// must act on goes in an Alert or a ConfirmDialog instead.
};

const actionTypes = {
	ADD_TOAST: 'ADD_TOAST',
	UPDATE_TOAST: 'UPDATE_TOAST',
	DISMISS_TOAST: 'DISMISS_TOAST',
	REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

let count = 0;

function genId() {
	count = (count + 1) % Number.MAX_SAFE_INTEGER;
	return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
	| {
			type: ActionType['ADD_TOAST'];
			toast: ToasterToast;
	  }
	| {
			type: ActionType['UPDATE_TOAST'];
			toast: Partial<ToasterToast>;
	  }
	| {
			type: ActionType['DISMISS_TOAST'];
			toastId?: ToasterToast['id'];
	  }
	| {
			type: ActionType['REMOVE_TOAST'];
			toastId?: ToasterToast['id'];
	  };

interface State {
	toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
	if (toastTimeouts.has(toastId)) {
		return;
	}

	const timeout = setTimeout(() => {
		toastTimeouts.delete(toastId);
		dispatch({
			type: 'REMOVE_TOAST',
			toastId: toastId,
		});
	}, TOAST_REMOVE_DELAY);

	toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'ADD_TOAST':
			return {
				...state,
				toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
			};

		case 'UPDATE_TOAST':
			return {
				...state,
				toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
			};

		case 'DISMISS_TOAST': {
			const { toastId } = action;

			// ! Side effects ! - This could be extracted into a dismissToast() action,
			// but I'll keep it here for simplicity
			if (toastId) {
				addToRemoveQueue(toastId);
			} else {
				state.toasts.forEach((toast) => {
					addToRemoveQueue(toast.id);
				});
			}

			return {
				...state,
				toasts: state.toasts.map((t) =>
					t.id === toastId || toastId === undefined
						? {
								...t,
								open: false,
							}
						: t,
				),
			};
		}
		case 'REMOVE_TOAST':
			if (action.toastId === undefined) {
				return {
					...state,
					toasts: [],
				};
			}
			return {
				...state,
				toasts: state.toasts.filter((t) => t.id !== action.toastId),
			};
	}
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
	memoryState = reducer(memoryState, action);
	listeners.forEach((listener) => {
		listener(memoryState);
	});
}

type Toast = Omit<ToasterToast, 'id'>;

/**
 * Rewrites the text of every toast before it shows. The product registers its own once at
 * startup (`setToastTextTransform(brandText)`), which keeps this file free of product config.
 */
let transformText: (text: string) => string = (text) => text;

function setToastTextTransform(fn: (text: string) => string) {
	transformText = fn;
}

function toast({ ...props }: Toast) {
	const id = genId();

	// Server copy names the product. Every AppError message the services raise
	// ("HumAIn not found for this room", "Concurrent HumAIn session limit of 3
	// reached") lands here verbatim, so the SALES build was showing the user a
	// product they do not have. This is the one funnel every toast goes through,
	// which is why the substitution lives here rather than at ~200 call sites.
	//
	// Only strings - title/description are ReactNodes and a rendered element is
	// not ours to rewrite. Static copy is already branded at build time
	// (vite.config.ts `deploymentSourceCopy`), and brandText is idempotent, so
	// running over it again changes nothing.
	if (typeof props.title === 'string') props.title = transformText(props.title);
	if (typeof props.description === 'string') {
		props.description = transformText(props.description);
	}

	let autoDismissTimer: ReturnType<typeof setTimeout> | undefined;
	const update = (props: ToasterToast) =>
		dispatch({
			type: 'UPDATE_TOAST',
			toast: { ...props, id },
		});
	const dismiss = () => {
		if (autoDismissTimer) clearTimeout(autoDismissTimer);
		dispatch({ type: 'DISMISS_TOAST', toastId: id });
	};

	dispatch({
		type: 'ADD_TOAST',
		toast: {
			...props,
			id,
			open: true,
			onOpenChange: (open) => {
				if (!open) dismiss();
			},
		},
	});

	// Auto-dismiss after the visible duration. Driven explicitly (not via Radix's own
	// duration timer) so it fires reliably regardless of re-renders or hover state.
	autoDismissTimer = setTimeout(dismiss, TOAST_AUTO_DISMISS_DELAY);

	return {
		id: id,
		dismiss,
		update,
	};
}

function useToast() {
	const [state, setState] = React.useState<State>(memoryState);

	React.useEffect(() => {
		listeners.push(setState);
		return () => {
			const index = listeners.indexOf(setState);
			if (index > -1) {
				listeners.splice(index, 1);
			}
		};
	}, [state]);

	return {
		...state,
		toast,
		dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
	};
}

export { useToast, toast, setToastTextTransform };
